/**
 * Shared donation helpers: turns an amount into the largest possible donation
 * variants and adds them to the cart.
 */
window.DonationCart = {
  parseVariants(element) {
    if (!element) return [];

    try {
      const variants = JSON.parse(element.textContent);
      const prices = new Set();

      return variants
        .filter((variant) => variant.available && variant.price > 0)
        .filter((variant) => {
          if (prices.has(variant.price)) return false;
          prices.add(variant.price);
          return true;
        })
        .map((variant) => ({ id: variant.id, price: variant.price }))
        .sort((a, b) => b.price - a.price);
    } catch (error) {
      console.error(error);
      return [];
    }
  },

  smallestUnit(units) {
    return units.length ? units[units.length - 1].price : 1;
  },

  roundToUnit(units, amount) {
    const unit = this.smallestUnit(units);
    return Math.round(amount / unit) * unit;
  },

  /**
   * £15 becomes 1 × £10 plus 5 × £1. Returns null when the amount cannot be
   * covered by the available variants.
   */
  buildItems(units, amount) {
    const items = [];
    let remaining = amount;

    for (const unit of units) {
      if (remaining < unit.price) continue;

      const quantity = Math.floor(remaining / unit.price);
      remaining -= unit.price * quantity;
      items.push({ id: unit.id, quantity });

      if (remaining === 0) break;
    }

    return remaining === 0 && items.length ? items : null;
  },

  formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'GBP';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch (error) {
      return (amount / 100).toFixed(2);
    }
  },

  getCart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  },

  /**
   * The cart drawer and notification expect the shape of a single line item,
   * while multi-item adds respond with an `items` array.
   */
  normalizeResponse(response, sections) {
    const addedItem = Array.isArray(response.items) ? response.items[0] : response;
    return { ...addedItem, items: response.items, sections: response.sections || sections };
  },

  async fetchSections(cart) {
    if (!cart) return undefined;

    const sections = cart
      .getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    try {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(sections)}`;
      return await fetch(url).then((response) => response.json());
    } catch (error) {
      console.error(error);
      return undefined;
    }
  },

  async addItems(items, activeElement) {
    const cart = this.getCart();
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const payload = { items };

    if (cart) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      cart.setActiveElement(activeElement || document.activeElement);
    }

    config.body = JSON.stringify(payload);

    const response = await fetch(routes.cart_add_url, config).then((result) => result.json());

    if (response.status) {
      throw new Error(response.description || response.message);
    }

    if (!cart) {
      window.location = routes.cart_url;
      return response;
    }

    const fallbackSections = response.sections ? undefined : await this.fetchSections(cart);
    const state = this.normalizeResponse(response, fallbackSections);

    await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'donation-cart', cartData: state });
    cart.renderContents(state);

    return state;
  },
};
